import React, { useCallback, useEffect, useRef, useState } from "react";
import { useServiceContext } from "@/res/context/ServiceContext";
import useEmailInput from "@/res/hooks/Form/useEmailInput";
import { Trans, useTranslation } from "react-i18next";
import useStringInput from "@/res/hooks/Form/useStringInput";
import usePhoneInput from "@/res/hooks/Form/usePhoneInput";
import { BrregUrl } from "@/res/helpers/BrregHelper";
import axios from "axios";
import useEventListener from "@/res/hooks/EventListener";
import useApi from "@/res/hooks/SynegaApi";
import { useRouter } from "next/router";
import Spinner from "@/res/components/LoadingIndicators/Spinner";
import useDateInput from "@/res/hooks/Form/useDateInput";
import useToggleInput from "@/res/hooks/Form/useToggleInput";
import { merge } from "lodash";

import ExpandSVG from "@/res/assets/icons/expand_arrow.svg";
import CollapseSVG from "@/res/assets/icons/collapse_arrow.svg";
import RestoreSVG from "@/res/assets/icons/time_machine.svg";
import StateOrgForm from "@/res/components/CountrySpecific/UnitedStates/StateOrgForm";
import { privacyPolicyUrl } from "@/res/helpers/ServiceHelper";
import InDevelopment from "@/res/components/ClientCreate/cards/in-development";
import * as Sentry from "@sentry/nextjs";

const SEARCH_LOCKOUT = 200;

const Card = (props) => {
    const [t] = useTranslation();
    const api = useApi();
    const api2 = useApi(2);
    const router = useRouter();
    const serviceContext = useServiceContext();

    const uploadingState = useState(false);
    const [uploading, setUploading] = uploadingState;

    const [isBusiness, setIsBusiness] = useState(props?.defaultMode === "BUSINESS" ?? true);
    const [details, setDetails] = useState({});
    const [detailsBackup, setDetailsBackup] = useState(null);
    const [searching, setSearching] = useState(false);
    const [lookupFocus, setLookupFocus] = useState(false);
    const [lastClick, setLastClick] = useState(Date.now())
    const lastClickRef = useRef(lastClick)
    const lookupUrlRef = useRef("");

    const [dirty, setDirty] = useState(false);
    const [companyDetailsExpanded, setCompanyDetailsExpanded] = useState(false);
    const [previousClient, setPreviousClient] = useState(null);
    const [companies, setCompanies] = useState(null);
    const [company, setCompany] = useState(null);
    const [brregStatus, setBrregStatus] = useState("working");
    const [formPage, setFormPage] = useState(1);

    const emailInput = useEmailInput({
        key: "client.contact_person.email", context: serviceContext, class: "small",
        label: t("_shared.form.email-label"), placeholder: t("_shared.form.email-placeholder"),
    });
    const nameInput = useStringInput({
        key: "client.contact_person.name", context: serviceContext, class: "small",
        label: t("_shared.form.name-label"), placeholder: t("_shared.form.name-placeholder"),
    });
    const phoneInput = usePhoneInput({
        key: "client.contact_person.phone_number", context: serviceContext, class: "small",
        label: t("_shared.form.phone-label"),
    });

    const amlCheckInput = useToggleInput({
        label: t("site.signup._country._service.index.perform-aml"), rule: { literals: true }, default: null,
        class: "tiny",
    });
    const surveyInput = useToggleInput({
        label: t("site.signup._country._service.index.is-new-client"), rule: { literals: true }, default: true,
        class: "tiny",
    });

    const streetInput = useStringInput({
        key: "company.address.street", context: serviceContext, class: "small",
        label: "Adresse", rule: { required: false },
    });
    const postCodeInput = useStringInput({
        key: "company.address.post_code", context: serviceContext, class: "small",
        label: "Postnummer", rule: { required: false },
    });
    const cityInput = useStringInput({
        key: "company.address.city", context: serviceContext, class: "small",
        label: "Poststed", rule: { required: false },
    });

    const naceInput = useStringInput({
        key: "company.nace_category", context: serviceContext, class: "small",
        label: "Næringskode", rule: { required: false },
    });
    const registrationDateInput = useDateInput({
        key: "company.registration_date", context: serviceContext, class: "small",
        label: "Registreringsdato", rule: { required: false },
    });
    const foundationDate = useDateInput({
        key: "company.foundation_date", context: serviceContext, class: "small",
        label: "Stiftelsesdato", rule: { required: false },
    });
    const vatRegisteredToggle = useToggleInput({
        key: "company.vat_registered", context: serviceContext, class: "small",
        label: "MVA Registrert", rule: { literals: true, required: false },
    });

    const confirmTermsInput = useToggleInput({
        context: serviceContext, key: "config.accept_terms", class: "minuscule",
        label: <Trans i18nKey="site.signup._country._service.index.agree-to-terms">
            <a href={privacyPolicyUrl(props?.country)} target="_blank" rel="noreferrer"/>
        </Trans>,
        rule: { required: true },
    });

    const keydownHandler = (e) => {
        if (e?.key === "Enter" && company !== null) {
            setLookupFocus(false);
        }
    }
    useEventListener("keydown", keydownHandler)

    const businessStructureValidate = (element, value) => {
        if (brregStatus !== "error") {
            element?.setCustomValidity?.("");
            return true;
        }

        // TODO internationalise
        if (!["AS", "ENK"].includes(value) && serviceContext?.state?.shared?.service_code === "ACNO") {
            element?.setCustomValidity?.("Selskapsformen må være av typen AS eller ENK.");
            return false;
        }

        element?.setCustomValidity?.("");
        return true;
    };

    const businessNumberValidate = (element, value) => {
        if (brregStatus !== "error") {
            element?.setCustomValidity?.("");
            return true;
        }

        let reg = /\D+/g;
        let newValue = value.replaceAll(reg, "");
        if (newValue?.length !== 9) {
            element?.setCustomValidity?.("Organisasjonsnummeret må være ni siffer (uten mellomrom).");
            return false;
        }

        element?.setCustomValidity?.("");
        return true;
    };

    const businessStructure = useStringInput({
        key: "company.structure", context: serviceContext, class: "small",
        label: "Selskapsform", placeholder: "AS / ENK", rule: { required: true },
        onValidation: businessStructureValidate,
    });

    const businessNumber = useStringInput({
        key: "company.number", context: serviceContext, class: "small",
        label: "Org. nummer", rule: { required: true },
        onValidation: businessNumberValidate,
    });

    useEffect(() => {
        let client = JSON.parse(localStorage.getItem(serviceContext?.state?.shared?.company_key));
        setPreviousClient(client);

        if (props?.isSales) {
            api2.get(`/contracts/aml/${ serviceContext?.state?.shared?.service_code?.toLowerCase?.() }`).then(res => {
                amlCheckInput.setValue(res?.data?.enabled ?? false)
            });
        }
    }, []);

    const clickHandler = useCallback((e) => {
        let activeElement = document.activeElement;

        if (!e.target.classList.contains("search-item") &&
            !e.target.classList.contains("search-field") &&
            (activeElement == null || !activeElement.classList.contains("search-field"))
        ) {
            setLookupFocus(false);
        }
    }, []);
    useEventListener("click", clickHandler);

    const fetchCompanyLookup = () => {
        let diff = Date.now() - lastClickRef.current;
        if (diff < SEARCH_LOCKOUT || (businessInput.value?.length ?? 0) < 3 || searching) {
            return;
        }

        let url = BrregUrl(businessInput.value);
        lookupUrlRef.current = url;

        axios({
            method: "GET",
            url: url,
        }).then(res => {
            if (res.status === 200) {
                let oldUrl = encodeURI(lookupUrlRef?.current?.split("?")?.[1]?.replace("navn=", "")?.replace("organisasjonsnummer=", ""));
                let resUrl = res.data?._links?.self?.href?.split("?")?.[1]?.replace("navn=", "")?.replace("organisasjonsnummer=", "");

                if (companies == null || oldUrl === resUrl) {
                    setCompanies(res.data?._embedded?.enheter ?? null);
                }
                setBrregStatus("working");
            } else {
                setBrregStatus("error");
            }
        }).catch(error => {
            console.log("(Brreg Error) ", error);
            setBrregStatus("error");
        });
    }

    const autoSelectCompany = () => {
        let diff = Date.now() - lastClickRef.current;
        if (diff < SEARCH_LOCKOUT || businessInput.value?.length < 5 || searching) {
            return;
        }

        let details = {
            name: businessInput.value,
            companyType: null,
            companyTypeName: null,
            companyId: null,
            numberOfEmployees: null,
            registrationDate: null,
            foundationDate: null,
            registeredForNav: null,
            registeredForVat: null,
            postalAddress: null,
            naceCategories: null,
        }

        setDetails(details);
        setCompany({});
    };

    const companyLookup = () => {
        let time = Date.now();
        setLastClick(time);
        lastClickRef.current = time;

        if (props?.isSearchable) {
            setTimeout(fetchCompanyLookup, SEARCH_LOCKOUT)
        } else {
            setTimeout(autoSelectCompany, SEARCH_LOCKOUT * 2.5)
        }
    };

    const businessInput = useStringInput({
        onKeyUp: companyLookup, onFocus: () => setLookupFocus(true),
        rule: { required: true, autoComplete: "off" }, class: "search-field",
    });

    useEffect(() => {
        if (brregStatus !== "error") return;

        let details = {
            name: businessInput.value?.toUpperCase?.(),
            companyType: businessStructure.value?.toUpperCase?.(),
            companyTypeName: null,
            companyId: businessNumber.value,
            numberOfEmployees: null,
            registrationDate: null,
            foundationDate: null,
            registeredForNav: null,
            registeredForVat: vatRegisteredToggle.value,
            postalAddress: {
                addressLine: null,
                postPlace: null,
                zipCode: null,
            },
            naceCategories: null,
        };

        setDetails(details);
    }, [businessNumber.value, businessStructure.value, businessInput.value, vatRegisteredToggle.value]);

    useEffect(() => {
        if (company != null) {
            if (dirty === "restored") {
                setDirty(false);
            } else {
                setDirty(true);
            }
        }
    }, [
        businessNumber.value, businessNumber.value, businessStructure.value,
        streetInput.value, cityInput.value, postCodeInput.value, naceInput.value,
        registrationDateInput.value, foundationDate.value, vatRegisteredToggle.value,
    ])

    useEffect(() => {
        let backup = JSON.parse(JSON.stringify(details));
        if (detailsBackup !== null) {
            if (Object.keys(detailsBackup ?? {}).length > 0) {
                setDetails(detailsBackup);
            } else {
                setDetails(merge(detailsBackup, {
                    name: businessInput.value,
                }));
            }
        }
        setDetailsBackup(backup);
    }, [isBusiness])

    useEffect(() => {
        if (isBusiness) {
            return;
        }
        setDetails(merge(details, {
            name: nameInput.value,
        }));
    }, [nameInput.value]);

    const resetCompanyValues = (data = null) => {
        let c = data ?? company;

        if (c != null && (dirty || data != null)) {
            setDirty("restored");

            businessInput.setValue(c.navn);
            businessNumber.setValue(c?.organisasjonsnummer ?? "");
            businessStructure.setValue(c?.organisasjonsform?.kode ?? "")
            streetInput.setValue(c?.forretningsadresse?.adresse?.[0] ?? "");
            cityInput.setValue(c?.forretningsadresse?.poststed ?? "");
            postCodeInput.setValue(c?.forretningsadresse?.postnummer ?? "");
            naceInput.setValue(c?.naeringskode1?.kode ?? "");
            registrationDateInput.setValue(c?.registreringsdatoEnhetsregisteret ?? "");
            foundationDate.setValue(c?.stiftelsesdato ?? "");
            vatRegisteredToggle.setValue(c?.registrertIMvaregisteret ?? "");

            setTimeout(() => {
                setDirty(false);
            }, 50);
        }
    };

    const selectCompany = (company) => {
        setLookupFocus(false);

        resetCompanyValues(company);

        let details = {
            name: company?.navn,
            companyType: company?.organisasjonsform.kode,
            companyTypeName: company?.organisasjonsform.beskrivelse,
            companyId: company?.organisasjonsnummer,
            numberOfEmployees: company?.antallAnsatte,
            registrationDate: company?.registreringsdatoEnhetsregisteret,
            foundationDate: company?.stiftelsesdato,
            registeredForNav: null,
            registeredForVat: company?.registrertIMvaregisteret,
            postalAddress: {
                addressLine: company?.forretningsadresse?.adresse?.[0],
                postPlace: company?.forretningsadresse?.poststed,
                zipCode: company?.forretningsadresse?.postnummer,
            },
            naceCategories: `${company?.naeringskode1?.kode} ${company?.naeringskode1?.beskrivelse}`,
        };

        signatoriesLookUp(company, details);
    }

    const signatoriesLookUp = (company, details) => {
        setSearching(true);

        api.post("/company/brreg/signatories", {
            companyId: details.companyId,
        }).then(res => {
            if (res.status === 200) {
                details.possibleSignatories = res.data.code === "OK" ? res.data.signatories : [];

                if (details.possibleSignatories?.length > 0) {
                    nameInput.setValue(details.possibleSignatories[0].name);
                } else {
                    nameInput.setValue("");
                }

                emailInput.setValue("");
                // phoneInput.setValue(""); TODO

                setDetails(details);
                setCompany(company);
                setDirty(false);
            }
        }).catch(() => {
            nameInput.setValue("");
            emailInput.setValue("");

            setDetails(details);
            setCompany(company);
            setDirty(false);
        }).finally(() => {
            setSearching(false);
        });
    };

    const upload = (e) => {
        e.preventDefault();
        if (lookupFocus) {
            if (companies.length > 0) {
                selectCompany(companies[0]);
            }
            return;
        }

        if (!props?.isSales && !confirmTermsInput.isValid()) return;
        if (brregStatus === "error" && formPage === 1) {
            setFormPage(2);
            return;
        }

        setUploading(true);

        if (["AUNO", "ACUS", "ACNO", "LENO", "SEIN"].includes(serviceContext?.state?.shared?.service_code)) {
            newUpload();
        } else {
            oldUpload();
        }
    };

    const newUpload = () => {
        api2.patch(`/clients/${serviceContext?.state?.shared?.reference}`, {
            operation: "initial",
            common: {
                force_aml: props?.isSales ? amlCheckInput.value : null,
                new_customer: surveyInput.value ?? true,
                category: (isBusiness ? "BUSINESS" : "PERSONAL"),
            },
            contact: {
                name: nameInput.value,
                email: emailInput.value,
                phone: phoneInput.value,
            },
            details: details,
            extra: serviceContext?.state?.extra ?? {},
            url_params: props?.urlParams ?? {},
        }).then(res => {
            if (res.status === "success") {
                res.data.client["version"] = 2;
                localStorage.setItem(serviceContext?.state?.shared?.company_key, JSON.stringify(res.data.client));
                localStorage.removeItem(serviceContext?.state?.shared?.client_id_key);
                localStorage.removeItem(serviceContext?.state?.shared?.client_payload_key);
                localStorage.removeItem(serviceContext?.state?.shared?.client_create_key);

                goToServiceCustomisation();
            } else {
                setUploading(false);
            }
        }).catch(error => {
            let url = null;
            for (let index in error?.response?.data?.messages) {
                let message = error?.response?.data?.messages[index];
                if (message === "Client already exists.") {
                    url = window.location.pathname + "company-exists";
                    break;
                }
            }

            if (url?.length > 0) {
                router.push(url);
            } else {
                Sentry.captureException(error);
                console.log(error?.response?.data?.messages);
                setUploading(false);
            }
        });
    };

    const oldUpload = () => {
        api.post("/client", {
            isBusiness: isBusiness,
            serviceCode: serviceContext?.state?.shared?.service_code,

            details: details,
            isSales: props?.isSales ?? false,
            checkAml: props?.isSales ? amlCheckInput.value : null,

            contact: {
                name: serviceContext?.state?.client?.contact_person?.name,
                dialCode: serviceContext?.state?.client?.contact_person?.phone_number?.dial_code,
                mobile: serviceContext?.state?.client?.contact_person?.phone_number?.number,
                email: serviceContext?.state?.client?.contact_person?.email,
            },
        }).then(res => {
            if (res.data.status === 200) {
                serviceContext?.dispatch({ type: serviceContext?.actions?.DELETE });

                if (isBusiness) {
                    handleCompanySubmission(res);
                } else {
                    handlePersonalSubmission(res);
                }
            } else {
                setUploading(false); // TODO remove
            }
        }).catch(err => {
            // TODO add some user feedback
            setUploading(false);
            Sentry.captureException(err);
        });
    };

    const handleCompanySubmission = res => {
        if (res.data.unsupportedCompanyType) {
            router.push(window.location.pathname + "unsupported-business-type");
        } else if (res.data.companyExists) {
            router.push(window.location.pathname + "company-exists");
        } else {
            res.data.company["isBusiness"] = true;
            let data = res.data.company;
            if (res.data.extra != null) {
                data["extra"] = res.data.extra;
            }

            localStorage.setItem(serviceContext?.state?.shared?.company_key, JSON.stringify(data));
            localStorage.removeItem(serviceContext?.state?.shared?.client_id_key);
            localStorage.removeItem(serviceContext?.state?.shared?.client_payload_key);

            goToServiceCustomisation();
        }
    }

    const handlePersonalSubmission = res => {
        res.data.personal["isBusiness"] = false;

        localStorage.setItem(serviceContext?.state?.shared?.company_key, JSON.stringify(res.data.personal));
        localStorage.removeItem(serviceContext?.state?.shared?.client_id_key);
        localStorage.removeItem(serviceContext?.state?.shared?.client_payload_key);

        goToServiceCustomisation();
    }

    const continueWithPreviousClient = () => {
        router.push(window.location.pathname + "customise");
    }

    const goToServiceCustomisation = () => {
        router.push(window.location.pathname + "customise");
    };

    const ModeToggle = useCallback(() => {
        return (
            <div className={ "type-container " + (props?.isMultiMode ? " type-container-toggleable" : "") }>
                <div className="type-buttons">
                    <div className={ "type-button" + (isBusiness ? " active" : "") } onClick={() => setIsBusiness(true)}>
                        {t("pages.start.form.modes.business")}
                    </div>
                    <div className="type-divider"/>
                    <div className={ "type-button" + (!isBusiness ? " active" : "") } onClick={() => setIsBusiness(false)}>
                        {t("pages.start.form.modes.private")}
                    </div>
                </div>
            </div>
        );
    }, [isBusiness, props?.isMultiMode]);

    if (props?.inDevelopment && !props?.isSales) {
        return <InDevelopment uploadingState={uploadingState}/>
    }

    if (!isBusiness) {
        return (
            <form className="card-container enable-pointer-events" onSubmit={upload}>
                <ModeToggle/>

                { uploading &&
                <Spinner/>
                }

                { !uploading &&
                <>
                    { nameInput.render() }
                    <div className="input-group">
                        { emailInput.render() }
                        { phoneInput.render() }
                    </div>

                    <div style={{ margin: "1.4rem 0" }}/> {/* TODO */ }
                    { confirmTermsInput.render() }

                    { previousClient?.isBusiness === false && (phoneInput.value?.number?.length ?? 0) > 0 ?
                        <div className="button-group">
                            <button className="btn stretch" type="button" onClick={continueWithPreviousClient}>
                                { t("_shared.button.continue_as", { value: previousClient?.name?.split?.(" ")?.[0] ?? "?" }) }
                            </button>
                        </div>
                        :
                        <div className="button-group">
                            <button className="btn">
                                { t("_shared.button.continue") }
                            </button>
                        </div>
                    }
                </>
                }
            </form>
        );
    }

    if (props?.isSales) {
        return (
            <form className="card-container enable-pointer-events" onSubmit={upload}>
                <ModeToggle/>

                { !uploading &&
                <div>
                    <h5 className="center">
                        {t("pages.start.form.select-business.header")}
                    </h5>
                    {businessInput.render()}
                    { props?.isSearchable && lookupFocus && companies?.length > 0 &&
                    <div className="search-container">
                        { companies.map((company, index) => {
                            return (
                                <div key={company?.organisasjonsnummer ?? index}>
                                    <div className="search-item" onClick={() => selectCompany(company)}>
                                        <strong>{ company.navn }</strong><br/>
                                        { company.organisasjonsnummer }
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    }
                </div>
                }

                { !company &&
                <>
                    { searching &&
                    <Spinner/>
                    }

                    { !searching && props?.isSearchable &&
                    <>
                        {t("pages.start.form.select-business.description")}
                    </>
                    }

                    { !searching && previousClient?.isBusiness === true &&
                    <>
                        <div style={{ margin: "2.1rem 0" }}/> {/* TODO */}
                        <div className="button-group">
                            <button className="btn stretch" type="button" onClick={continueWithPreviousClient}>
                                { t("_shared.button.continue_as", { value: previousClient?.name ?? "?" }) }
                            </button>
                        </div>
                    </>
                    }
                </>
                }

                { company &&
                <>
                    { uploading &&
                    <Spinner/>
                    }

                    { !uploading &&
                    <>
                        { props?.isSearchable &&
                        <div className={ "inner-container" + (companyDetailsExpanded ? " show" : "") }>
                            <div className="header" onClick={ () => setCompanyDetailsExpanded(!companyDetailsExpanded) }>
                                <label>Bedriftens detaljer</label>
                                { companyDetailsExpanded ?
                                    <img src={ CollapseSVG } alt=""/>
                                    :
                                    <img src={ ExpandSVG } alt=""/>
                                }
                            </div>
                            <img
                                src={RestoreSVG}
                                className={ "restore-button" + (dirty ? " dirty" : "") }
                                onClick={() => resetCompanyValues()}
                                alt="Reset"
                            />

                            { vatRegisteredToggle.render() }

                            <div className="input-group always">
                                { businessNumber.render() }
                                { businessStructure.render() }
                            </div>

                            { naceInput.render() }

                            <div className="input-group always">
                                { registrationDateInput.render() }
                                { foundationDate.render() }
                            </div>

                            { streetInput.render() }
                            <div className="input-group always">
                                { postCodeInput.render() }
                                { cityInput.render() }
                            </div>
                        </div>
                        }

                        { nameInput.render() }
                        <div className="input-group">
                            { emailInput.render() }
                            { phoneInput.render() }
                        </div>

                        <div style={{ margin: "0.9rem 0" }}/> {/* TODO */ }
                        { props?.isAmlEnabled && amlCheckInput.render() }
                        { surveyInput.render() }

                        <div style={{ margin: "1.4rem 0" }}/> {/* TODO */ }
                        <div className="button-group">
                            <button className="btn">
                                {t("_shared.button.continue")}
                            </button>
                        </div>
                    </>
                    }
                </>
                }
            </form>
        );
    }

    return (
        <form className="card-container enable-pointer-events" onSubmit={upload}>
            <ModeToggle/>

            { !uploading &&
            <div>
                <h5 className="center">
                    {t("pages.start.form.select-business.header")}
                </h5>
                { (brregStatus !== "error" || (brregStatus === "error" && formPage === 1)) &&
                businessInput.render()
                }
                { brregStatus === "error" && formPage === 1 &&
                <>
                    <div className="input-group always">
                        { businessNumber.render() }
                        { businessStructure.render() }
                    </div>
                    { vatRegisteredToggle.render() }
                </>
                }
                { brregStatus === "error" && formPage === 2 &&
                <div className="input-container">
                    <div className="pretend-input">
                        <div className="title">
                            {businessInput.value}
                        </div>
                        <div className="miscellaneous">
                            {businessNumber.value}, {businessStructure.value}
                        </div>
                    </div>
                </div>
                }

                { props?.isSearchable && lookupFocus && companies?.length > 0 &&
                <div className="search-container">
                    { companies.map((company, index) => {
                        return (
                            <div key={company?.organisasjonsnummer ?? index}>
                                <div className="search-item" onClick={() => selectCompany(company)}>
                                    <strong>{ company.navn }</strong><br/>
                                    { company.organisasjonsnummer }
                                </div>
                            </div>
                        );
                    })}
                </div>
                }
            </div>
            }

            { !company && formPage === 1 &&
            <>
                { searching &&
                <Spinner/>
                }

                { (!uploading && !searching && props?.isSearchable) &&
                <>
                    { (brregStatus == null || brregStatus === "working") ?
                        t("pages.start.form.select-business.description")
                        :
                        t("pages.start.form.select-business.description-error")
                    }
                    <div style={{ margin: "1.4rem 0" }}/> {/* TODO */ }
                </>
                }

                { !searching && previousClient?.isBusiness === true &&
                <>
                    <div style={{ margin: "2.1rem 0" }}/> {/* TODO */}
                    <div className="button-group">
                        <button className="btn stretch" type="button" onClick={continueWithPreviousClient}>
                            { t("_shared.button.continue_as", { value: previousClient?.name ?? "?" }) }
                        </button>
                    </div>
                </>
                }

                { brregStatus === "error" &&
                <>
                    <div style={{ margin: "1.4rem 0" }}/> {/* TODO */ }
                    <div className="button-group button-group-space-around">
                        <button className="btn">
                            {t("_shared.button.continue")}
                        </button>
                    </div>
                </>
                }
            </>
            }

            { (company || (brregStatus === "error" && formPage === 2)) &&
            <>
                { uploading &&
                <Spinner/>
                }

                { !uploading &&
                <>
                    { props?.country?.toUpperCase() === "US" &&
                        <StateOrgForm/>
                    }
                    { nameInput.render() }
                    <div className="input-group">
                        { emailInput.render() }
                        { phoneInput.render() }
                    </div>

                    <div style={{ margin: "1.4rem 0" }}/> {/* TODO */ }
                    { confirmTermsInput.render() }

                    <div className="button-group button-group-space-around">
                        { brregStatus === "error" &&
                        <button className="btn stretch medium" onClick={() => setFormPage(1)}>
                            {t("_shared.button.return")}
                        </button>
                        }
                        <button className={ "btn" + (brregStatus === "error" ? " stretch medium" : "") }>
                            {t("_shared.button.continue")}
                        </button>
                    </div>
                </>
                }
            </>
            }
        </form>
    );
};

export default Card;
